import React, { useEffect } from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { Controller, UseFormReturn } from 'react-hook-form';

import { IAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import { TabOptions } from 'components/common/CustomFilters/DateRangeFilter/DateRangeFilter.utils';
import { HasCommentsFilter as HasCommentsInputFilter } from 'components/common/CustomFilters/HasCommentsFilter/hasCommentsFilter';
import classes from 'components/common/GlobalFiltersInPage/Filters.module.scss';
import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import {
    DEFAULT_EMPTY_DATA,
    hasCommentFixedDateMenuOptions,
    hasCommentsTodayMenuOptions,
    hasCommentsVersionDateMenuOptions,
} from 'components/common/GlobalFiltersInPage/components/hasCommentsFilter/HasCommentsFilter.consts';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

interface Data {
    value?: string | string[] | undefined;
    type?: TabOptions | undefined;
    commenter: IAssigneeOptionsList | null;
    mentioned: IAssigneeOptionsList | null;
}

interface Props {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    value?: Data;
    isSelected?: boolean;
}

type InputValueForHasCommentsData = {
    type: string | undefined;
    value: string | string[] | undefined;
    commenter: IAssigneeOptionsList | null;
    mentioned: IAssigneeOptionsList | null;
};

export const getInputValueForHasComments = (data: InputValueForHasCommentsData) => {
    if (!data?.value) return '';

    const commenterLabel = data.commenter ? ` Commenter: ${data.commenter.label}` : undefined;
    const mentionedLabel = data.mentioned ? ` Mentioned: ${data.mentioned.label}` : undefined;

    if (data.type === 'FIXED_DATE' && data.value) {
        const fixedDate = `${moment(Number(data.value[0])).format(constants.formats.date.default)} - ${moment(
            Number(data.value[1]),
        ).format(constants.formats.date.default)}`;

        return [fixedDate, commenterLabel, mentionedLabel].filter(Boolean).join();
    }

    switch (data.type) {
        case 'TODAY_REF':
            const todayRef = `${hasCommentsTodayMenuOptions.find((item) => item.value === data.value)?.inputLabel || ''}`;
            return [todayRef, commenterLabel, mentionedLabel].filter(Boolean).join();
        case 'VERSION_DATE_REF':
            const versionDateRef = `${hasCommentsVersionDateMenuOptions.find((item) => item.value === data.value)?.inputLabel || ''}`;
            return [versionDateRef, commenterLabel, mentionedLabel].filter(Boolean).join();
        default:
            return '';
    }
};
export const HasCommentsFilter = ({
    name,
    methods,
    filtersStatusRef,
    handleFilters,
    value,
    isSelected = true,
}: Props) => {
    useEffect(() => {
        if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady) {
            return;
        }

        signalSingleFilterIsReady({ filterId: name, isReady: true });
    }, [value, name, filtersStatusRef]);

    const handleHasCommentsDate = (data: {
        value?: string[] | string;
        type?: TabOptions;
        commenter: IAssigneeOptionsList | null;
        mentioned: IAssigneeOptionsList | null;
    }) => {
        methods?.setValue(name, data);
        handleFilters?.();
    };

    return isSelected ? (
        <div className={classes.selectWrapper}>
            <Controller
                render={() => (
                    <HasCommentsInputFilter
                        onChange={handleHasCommentsDate}
                        value={value ?? DEFAULT_EMPTY_DATA}
                        inputDefaultValue={getInputValueForHasComments(value)}
                        fixedDateMenuOptions={hasCommentFixedDateMenuOptions}
                        todayMenuOptions={hasCommentsTodayMenuOptions}
                        versionDateMenuOptions={hasCommentsVersionDateMenuOptions}
                        watch={methods?.watch}
                        label={FilterLabels.hasComments}
                    />
                )}
                name={name}
                control={methods?.control}
            />
        </div>
    ) : null;
};
